import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type {
  VerifyInstallationCheckSchema,
  VerifyInstallationResponseSchema,
} from "@polifonia/interlude/api/channel";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";

type CheckItem = z.infer<typeof VerifyInstallationCheckSchema>;
type VerifyInstallationResponse = z.infer<
  typeof VerifyInstallationResponseSchema
>;

// --- Styles ---

const styles = {
  checkRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "12px 0",
    borderBottom: `1px solid ${theme.colors.neutral020}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  statusIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
    flexShrink: 0,
  },
  suggestionBox: {
    padding: "8px 12px",
    borderRadius: "4px",
    backgroundColor: theme.colors.staticBgNegative,
    marginTop: "4px",
  },
  detailsToggle: {
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: theme.colors.staticFgPrimary,
    fontSize: "0.875rem",
    background: "none",
    border: "none",
    padding: 0,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  detailsContent: {
    padding: "8px 12px",
    borderRadius: "4px",
    backgroundColor: theme.colors.staticBgContainer,
    fontSize: "0.875rem",
    marginTop: "4px",
  },
  detailsItem: {
    padding: "2px 0",
    fontFamily: "monospace",
    fontSize: "0.8125rem",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },
  screenshotContainer: {
    marginTop: "16px",
    borderTop: `1px solid ${theme.colors.neutral020}`,
    paddingTop: "16px",
  },
  screenshotImage: {
    maxWidth: "100%",
    borderRadius: "4px",
    border: `1px solid ${theme.colors.neutral020}`,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const statusIconMap = {
  pass: { motif: "circle_check", color: theme.colors.green060 },
  fail: { motif: "circle_cross", color: theme.colors.red060 },
  warning: { motif: "warning", color: theme.colors.yellow060 },
  info: { motif: "circle_info", color: theme.colors.blue060 },
  skip: { motif: "circle_cross", color: theme.colors.neutral060 },
} as const satisfies Record<
  CheckItem["status"],
  { motif: string; color: string }
>;

// --- Components ---

const CheckStatusIcon: React.FC<{ status: CheckItem["status"] }> = ({
  status,
}) => {
  const { motif, color } = statusIconMap[status];
  return (
    <Box component="span" sx={{ ...styles.statusIcon, color }}>
      <MotifIcon un-i-motif={motif} />
    </Box>
  );
};

const CollapsibleToggle: React.FC<{
  label: string;
  sx?: SxProps<typeof muiTheme>;
  children: React.ReactNode;
}> = ({ label, sx, children }) => {
  const [expanded, setExpanded] = useState(false);
  const arrowMotif = expanded ? "arrow_up" : "arrow_down";

  return (
    <Box {...(sx != null && { sx })}>
      <Box
        component="button"
        type="button"
        sx={styles.detailsToggle}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setExpanded((prev) => !prev);
        }}
      >
        <Box component="span" sx={{ fontSize: "0.75rem" }}>
          <MotifIcon un-i-motif={arrowMotif} />
        </Box>
        {label}
      </Box>
      <Collapse in={expanded}>{children}</Collapse>
    </Box>
  );
};

const DetailsList: React.FC<{ items: Array<string> }> = ({ items }) => (
  <Box sx={styles.detailsContent}>
    {items.map((item, i) => (
      <Box key={i} sx={styles.detailsItem}>
        {item}
      </Box>
    ))}
  </Box>
);

const CheckRow: React.FC<{ check: CheckItem }> = ({ check }) => {
  const { t } = useTranslation();
  const ctx = (check.context ?? {}) as Record<string, unknown>;

  // Check names — no interpolation needed, computed for all IDs so skip
  // status can resolve its reasonId. Explicit t() keys for i18n-ally preview.
  const checkNames: Record<string, string> = {
    domain_whitelisted: t(
      "page.channel.wccs.install.verify.check.domain_whitelisted.name",
    ),
    sdk_global_exists: t(
      "page.channel.wccs.install.verify.check.sdk_global_exists.name",
    ),
    sdk_instance_initialized: t(
      "page.channel.wccs.install.verify.check.sdk_instance_initialized.name",
    ),
    channel_id_matches: t(
      "page.channel.wccs.install.verify.check.channel_id_matches.name",
    ),
    auth_succeeded: t(
      "page.channel.wccs.install.verify.check.auth_succeeded.name",
    ),
    channel_config_loaded: t(
      "page.channel.wccs.install.verify.check.channel_config_loaded.name",
    ),
    feature_chat_active: t(
      "page.channel.wccs.install.verify.check.feature_chat_active.name",
    ),
    feature_tracking_active: t(
      "page.channel.wccs.install.verify.check.feature_tracking_active.name",
    ),
    feature_srt_active: t(
      "page.channel.wccs.install.verify.check.feature_srt_active.name",
    ),
    widget_dom_rendered: t(
      "page.channel.wccs.install.verify.check.widget_dom_rendered.name",
    ),
    console_errors: t(
      "page.channel.wccs.install.verify.check.console_errors.name",
    ),
    network_failures: t(
      "page.channel.wccs.install.verify.check.network_failures.name",
    ),
    page_load: t("page.channel.wccs.install.verify.check.page_load.name"),
    url_validation: t(
      "page.channel.wccs.install.verify.check.url_validation.name",
    ),
    browser_error: t(
      "page.channel.wccs.install.verify.check.browser_error.name",
    ),
    timeout: t("page.channel.wccs.install.verify.check.timeout.name"),
  };

  // Status messages & suggestions — only computed for the current check.id
  // so ctx interpolation is correct. Explicit t() keys for i18n-ally preview.
  const checkStatusTexts: Record<
    string,
    Partial<Record<CheckItem["status"] | "suggestion", string>>
  > = {
    domain_whitelisted: {
      pass: t(
        "page.channel.wccs.install.verify.check.domain_whitelisted.pass",
        { ...ctx },
      ),
      fail: t(
        "page.channel.wccs.install.verify.check.domain_whitelisted.fail",
        { ...ctx },
      ),
      suggestion: t(
        "page.channel.wccs.install.verify.check.domain_whitelisted.suggestion",
      ),
    },
    sdk_global_exists: {
      pass: t("page.channel.wccs.install.verify.check.sdk_global_exists.pass"),
      fail: t("page.channel.wccs.install.verify.check.sdk_global_exists.fail"),
      suggestion: t(
        "page.channel.wccs.install.verify.check.sdk_global_exists.suggestion",
      ),
    },
    sdk_instance_initialized: {
      pass: t(
        "page.channel.wccs.install.verify.check.sdk_instance_initialized.pass",
        { ...ctx },
      ),
      fail: t(
        "page.channel.wccs.install.verify.check.sdk_instance_initialized.fail",
        { ...ctx },
      ),
      suggestion: t(
        "page.channel.wccs.install.verify.check.sdk_instance_initialized.suggestion",
      ),
    },
    channel_id_matches: {
      pass: t(
        "page.channel.wccs.install.verify.check.channel_id_matches.pass",
        { ...ctx },
      ),
      fail: t(
        "page.channel.wccs.install.verify.check.channel_id_matches.fail",
        { ...ctx },
      ),
      suggestion: t(
        "page.channel.wccs.install.verify.check.channel_id_matches.suggestion",
      ),
    },
    auth_succeeded: {
      pass: t("page.channel.wccs.install.verify.check.auth_succeeded.pass"),
      fail: t("page.channel.wccs.install.verify.check.auth_succeeded.fail"),
      suggestion: t(
        "page.channel.wccs.install.verify.check.auth_succeeded.suggestion",
      ),
    },
    channel_config_loaded: {
      pass: t(
        "page.channel.wccs.install.verify.check.channel_config_loaded.pass",
      ),
      fail: t(
        "page.channel.wccs.install.verify.check.channel_config_loaded.fail",
      ),
      suggestion: t(
        "page.channel.wccs.install.verify.check.channel_config_loaded.suggestion",
      ),
    },
    feature_chat_active: {
      pass: t(
        "page.channel.wccs.install.verify.check.feature_chat_active.pass",
      ),
      info: t(
        "page.channel.wccs.install.verify.check.feature_chat_active.info",
      ),
    },
    feature_tracking_active: {
      pass: t(
        "page.channel.wccs.install.verify.check.feature_tracking_active.pass",
      ),
      info: t(
        "page.channel.wccs.install.verify.check.feature_tracking_active.info",
      ),
    },
    feature_srt_active: {
      pass: t("page.channel.wccs.install.verify.check.feature_srt_active.pass"),
      info: t("page.channel.wccs.install.verify.check.feature_srt_active.info"),
    },
    widget_dom_rendered: {
      pass: t(
        "page.channel.wccs.install.verify.check.widget_dom_rendered.pass",
      ),
      info: t(
        "page.channel.wccs.install.verify.check.widget_dom_rendered.info",
      ),
    },
    console_errors: {
      pass: t("page.channel.wccs.install.verify.check.console_errors.pass"),
      warning: t(
        "page.channel.wccs.install.verify.check.console_errors.warning",
        { ...ctx },
      ),
    },
    network_failures: {
      pass: t("page.channel.wccs.install.verify.check.network_failures.pass"),
      warning: t(
        "page.channel.wccs.install.verify.check.network_failures.warning",
        { ...ctx },
      ),
    },
    page_load: {
      fail: t("page.channel.wccs.install.verify.check.page_load.fail"),
      suggestion: t(
        "page.channel.wccs.install.verify.check.page_load.suggestion",
      ),
    },
    url_validation: {
      fail: t("page.channel.wccs.install.verify.check.url_validation.fail"),
      suggestion: t(
        "page.channel.wccs.install.verify.check.url_validation.suggestion",
      ),
    },
    browser_error: {
      fail: t("page.channel.wccs.install.verify.check.browser_error.fail"),
      suggestion: t(
        "page.channel.wccs.install.verify.check.browser_error.suggestion",
      ),
    },
    timeout: {
      fail: t("page.channel.wccs.install.verify.check.timeout.fail", {
        ...ctx,
      }),
      suggestion: t(
        "page.channel.wccs.install.verify.check.timeout.suggestion",
      ),
    },
  };

  const name = checkNames[check.id] ?? check.id;

  const message =
    check.status === "skip" && typeof ctx["reasonId"] === "string"
      ? t("page.channel.wccs.install.verify.check.skip", {
          defaultValue: "Skipped: {{reason}}",
          reason: checkNames[ctx["reasonId"] as string] ?? ctx["reasonId"],
        })
      : (checkStatusTexts[check.id]?.[check.status] ?? "");

  const suggestion =
    check.status === "fail"
      ? (checkStatusTexts[check.id]?.suggestion ?? null)
      : null;

  // Render list details for checks with array context
  const listItems = Array.isArray(ctx["errors"])
    ? (ctx["errors"] as Array<string>)
    : Array.isArray(ctx["failures"])
      ? (ctx["failures"] as Array<string>)
      : null;

  const domainList =
    check.id === "domain_whitelisted" &&
    check.status === "fail" &&
    Array.isArray(ctx["domains"])
      ? (ctx["domains"] as Array<string>)
      : null;

  return (
    <Box sx={styles.checkRow}>
      <Stack direction="row" gap="8px" alignItems="center">
        <CheckStatusIcon status={check.status} />
        <Typography
          variant="body"
          fontWeight={500}
          color={theme.colors.staticFgTitle}
        >
          {name}
        </Typography>
      </Stack>
      <Box pl="24px">
        <Typography variant="note" color={theme.colors.staticFgBody}>
          {message}
        </Typography>
        {suggestion ? (
          <Box sx={styles.suggestionBox}>
            <Typography variant="note" color={theme.colors.staticFgNegative}>
              {suggestion}
            </Typography>
          </Box>
        ) : null}
        {listItems ? (
          <CollapsibleToggle
            label={t("page.channel.wccs.install.verify.details")}
          >
            <DetailsList items={listItems} />
          </CollapsibleToggle>
        ) : null}
        {domainList ? (
          <CollapsibleToggle
            label={t("page.channel.wccs.install.verify.registeredDomains")}
          >
            <DetailsList items={domainList} />
          </CollapsibleToggle>
        ) : null}
      </Box>
    </Box>
  );
};

const DiagnosticResult: React.FC<{
  result: VerifyInstallationResponse;
}> = ({ result }) => {
  const { t } = useTranslation();

  const totalChecks = result.checks.length;
  let passCount = 0;
  let failCount = 0;
  for (const c of result.checks) {
    if (c.status === "pass") passCount++;
    else if (c.status === "fail") failCount++;
  }

  return (
    <Stack direction="column" gap="8px">
      <Stack direction="row" gap="8px" alignItems="center">
        <CheckStatusIcon status={failCount === 0 ? "pass" : "warning"} />
        <Typography variant="body" color={theme.colors.staticFgTitle}>
          {failCount === 0
            ? t("page.channel.wccs.install.verify.result.allPass", {
                passCount,
                totalChecks,
              })
            : t("page.channel.wccs.install.verify.result.someFailures", {
                passCount,
                totalChecks,
                failCount,
              })}
        </Typography>
      </Stack>
      <Box>
        {result.checks.map((check) => (
          <CheckRow key={check.id} check={check} />
        ))}
      </Box>
      {result.screenshot ? (
        <Box sx={styles.screenshotContainer}>
          <Typography variant="note" color={theme.colors.staticFgBody}>
            {t("page.channel.wccs.install.verify.screenshot")}
          </Typography>
          <Box mt="8px">
            <Box
              component="img"
              src={`data:image/jpeg;base64,${result.screenshot}`}
              alt="Page screenshot"
              sx={styles.screenshotImage}
            />
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};

export { DiagnosticResult };
